import { useQuery } from '@tanstack/react-query'
import config from '@config'

const ALLOWED_TYPES_OF_SPORT = ['soccer'] as const

export type AllowedTypeOfSport = (typeof ALLOWED_TYPES_OF_SPORT)[number]
interface UseSportCompetitorsListProps {
  typeOfSport: 'soccer'
  seasonId: string
}

export interface SportCompetitor {
  sportradarId: string
  name: string
  abbreviation: string
  image: {
    src: string
    alt: string
  }
}

export interface SportCompetitorsListAPIResponse {
  items: SportCompetitor[]
}

const {
  sports: { baseUrlV2: sportsBaseUrlV2, sportCompetitorsListUrlTemplateV2 },
} = config

export const validSeasonId = (
  seasonId: UseSportCompetitorsListProps['seasonId']
) => !!seasonId && seasonId !== '0'

export const validTypeOfSport = (
  typeOfSport: UseSportCompetitorsListProps['typeOfSport']
) => ALLOWED_TYPES_OF_SPORT.includes(typeOfSport)

const fetchSportCompetitorsListData = async ({
  typeOfSport,
  seasonId,
}: UseSportCompetitorsListProps) => {
  const requestUrl = `${sportsBaseUrlV2}${sportCompetitorsListUrlTemplateV2
    .replace('{SPORT}', typeOfSport)
    .replace('{API_VERSION}', 'v0')
    .replace('{SEASON_ID}', seasonId)}`

  const response = await fetch(requestUrl)

  if (!response.ok) {
    throw new Error('Error fetching competitor data.')
  }

  return response.json() as Promise<SportCompetitorsListAPIResponse>
}

const useSportCompetitorsList = ({
  seasonId,
  typeOfSport,
}: UseSportCompetitorsListProps) => {
  const isEnabled = validSeasonId(seasonId) && validTypeOfSport(typeOfSport)

  return useQuery({
    queryKey: ['sportCompetitorsList', typeOfSport, seasonId],
    queryFn: () =>
      fetchSportCompetitorsListData({
        typeOfSport,
        seasonId,
      }),
    enabled: isEnabled,
  })
}

export default useSportCompetitorsList
